import type { HttpErrorResponse } from '@angular/common/http';

import { createAction, props } from '@ngrx/store';

import type { Position } from '@core/http-types';
import type {
  InsertFromTemplateMethod,
  OfferData,
  OfferFull,
  OfferStatus,
  StatusChangeType,
} from '@core/offers-types';
import { PlacementMovement } from '@core/stages-types';

const namespace = 'Jobs';

const getActionType = (name: string) => `[${namespace}] ${name}`;

const actions = {
  fetchCollection: createAction(getActionType('fetchCollection')),
  fetchCollectionSuccess: createAction(getActionType('fetchCollectionSuccess')),
  fetchCollectionFailed: createAction(
    getActionType('fetchCollectionFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  fetchActiveCollection: createAction(getActionType('fetchActiveCollection')),
  fetchActiveCollectionSuccess: createAction(getActionType('fetchActiveCollectionSuccess')),
  fetchActiveCollectionFailed: createAction(
    getActionType('fetchActiveCollectionFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  fetchArchivedCollection: createAction(getActionType('fetchArchivedCollection')),
  fetchArchivedCollectionSuccess: createAction(getActionType('fetchArchivedCollectionSuccess')),
  fetchArchivedCollectionFailed: createAction(
    getActionType('fetchArchivedCollectionFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  fetchFullOne: createAction(
    getActionType('fetchFullOne'),
    props<{ payload: { id: number | string; langCode?: string } }>(),
  ),
  fetchFullOneSuccess: createAction(
    getActionType('fetchFullOneSuccess'),
    props<{ payload: { id: number | string; job: OfferFull } }>(),
  ),
  fetchFullOneFailed: createAction(
    getActionType('fetchFullOneFailed'),
    props<{ payload: { id: number | string; error: HttpErrorResponse } }>(),
  ),

  jobUpdatedFromWebsocket: createAction(
    getActionType('jobUpdatedFromWebsocket'),
    props<{ payload: { id: number | string } }>(),
  ),
  jobUpdatedFromWebsocketSuccess: createAction(
    getActionType('jobUpdatedFromWebsocketSuccess'),
    props<{ payload: { id: number | string; job: OfferFull } }>(),
  ),
  jobUpdatedFromWebsocketFailed: createAction(
    getActionType('jobUpdatedFromWebsocketFailed'),
    props<{ payload: { id: number | string; error: HttpErrorResponse } }>(),
  ),

  jobCreatedFromWebsocket: createAction(
    getActionType('jobCreatedFromWebsocket'),
    props<{ payload: { id: number | string } }>(),
  ),
  jobCreatedFromWebsocketSuccess: createAction(
    getActionType('jobCreatedFromWebsocketSuccess'),
    props<{ payload: { id: number | string; job: OfferFull } }>(),
  ),
  jobCreatedFromWebsocketFailed: createAction(
    getActionType('jobCreatedFromWebsocketFailed'),
    props<{ payload: { id: number | string; error: HttpErrorResponse } }>(),
  ),

  jobDeletedFromWebsocket: createAction(
    getActionType('jobDeletedFromWebsocket'),
    props<{ payload: { id: number } }>(),
  ),
  jobDeletedFromWebsocketSuccess: createAction(
    getActionType('jobDeletedFromWebsocketSuccess'),
    props<{ payload: { id: number } }>(),
  ),
  jobDeletedFromWebsocketFailed: createAction(
    getActionType('jobDeletedFromWebsocketFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  jobsUpdatedFromWebsocket: createAction(getActionType('jobsUpdatedFromWebsocket')),
  jobsUpdatedFromWebsocketSuccess: createAction(getActionType('jobsUpdatedFromWebsocketSuccess')),
  jobsUpdatedFromWebsocketFailed: createAction(
    getActionType('jobsUpdatedFromWebsocketFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  updateFull: createAction(
    getActionType('updateFull'),
    props<{ payload: { offer: OfferData & { id: number } } }>(),
  ),
  updateFullSuccess: createAction(
    getActionType('updateFullSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  updateFullFailed: createAction(
    getActionType('updateFullFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  follow: createAction(getActionType('follow'), props<{ payload: { id: number } }>()),
  followSuccess: createAction(
    getActionType('followSuccess'),
    props<{ payload: { followed: boolean } }>(),
  ),
  followFailed: createAction(
    getActionType('followFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  unfollow: createAction(getActionType('unfollow'), props<{ payload: { id: number } }>()),
  unfollowSuccess: createAction(
    getActionType('unfollowSuccess'),
    props<{ payload: { followed: boolean } }>(),
  ),
  unfollowFailed: createAction(
    getActionType('unfollowFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  duplicate: createAction(getActionType('duplicate'), props<{ payload: { id: number } }>()),
  duplicateSuccess: createAction(
    getActionType('duplicateSuccess'),
    props<{ payload: { offer: OfferFull } }>(),
  ),
  duplicateFailed: createAction(
    getActionType('duplicateFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  create: createAction(getActionType('create'), props<{ payload: { offer: OfferData } }>()),
  createSuccess: createAction(
    getActionType('createSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFailed: createAction(
    getActionType('createFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  createFromRequisition: createAction(
    getActionType('createFromRequisition'),
    props<{ payload: { requisitionId: number } }>(),
  ),
  createFromRequisitionSuccess: createAction(
    getActionType('createFromRequisitionSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFromRequisitionFailed: createAction(
    getActionType('createFromRequisitionFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  createFromTemplate: createAction(
    getActionType('createFromTemplate'),
    props<{ payload: { templateId: number } }>(),
  ),
  createFromTemplateSuccess: createAction(
    getActionType('createFromTemplateSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFromTemplateFailed: createAction(
    getActionType('createFromTemplateFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  createFromPosition: createAction(
    getActionType('createFromPosition'),
    props<{ payload: { positionId: number } }>(),
  ),
  createFromPositionSuccess: createAction(
    getActionType('createFromPositionSuccess'),
    props<{ payload: { data: OfferFull } }>(),
  ),
  createFromPositionFailed: createAction(
    getActionType('createFromPositionFailed'),
    props<{ payload: { error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  changeStatus: createAction(
    getActionType('changeStatus'),
    props<{ payload: { id: number; status: StatusChangeType; currentStatus?: OfferStatus } }>(),
  ),
  changeStatusSuccess: createAction(
    getActionType('changeStatusSuccess'),
    props<{ payload: { offer: OfferFull } }>(),
  ),
  changeStatusFailed: createAction(
    getActionType('changeStatusFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse & { errorFields: any } } }>(),
  ),

  removeOne: createAction(getActionType('removeOne'), props<{ payload: { id: number } }>()),
  removeOneSuccess: createAction(
    getActionType('removeOneSuccess'),
    props<{ payload: { id: number } }>(),
  ),
  removeOneFailed: createAction(
    getActionType('removeOneFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  preview: createAction(getActionType('preview'), props<{ payload: { offer: OfferData } }>()),
  previewSuccess: createAction(
    getActionType('previewSuccess'),
    props<{ payload: { previewUrl: string } }>(),
  ),
  previewFailed: createAction(
    getActionType('previewFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  updatePositions: createAction(
    getActionType('updatePositions'),

    props<{ payload: Position[] }>(),
  ),
  updatePositionsSuccess: createAction(getActionType('updatePositionsSuccess')),
  updatePositionsFailed: createAction(
    getActionType('updatePositionsFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  uploadSocialMediaImage: createAction(
    getActionType('uploadSocialMediaImage'),
    props<{ payload: { id: number; file: File } }>(),
  ),
  uploadSocialMediaImageSuccess: createAction(
    getActionType('uploadSocialMediaImageSuccess'),
    props<{ payload: { fileName: string; url: string } }>(),
  ),
  uploadSocialMediaImageFailed: createAction(
    getActionType('uploadSocialMediaImageFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  uploadCoverImage: createAction(
    getActionType('uploadCoverImage'),
    props<{ payload: { id: number; file: File } }>(),
  ),
  uploadCoverImageSuccess: createAction(
    getActionType('uploadCoverImageSuccess'),
    props<{ payload: { fileName: string; url: string } }>(),
  ),
  uploadCoverImageFailed: createAction(
    getActionType('uploadCoverImageFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  updateSlug: createAction(
    getActionType('updateSlug'),
    props<{ payload: { id: number; slug: string } }>(),
  ),
  updateSlugSuccess: createAction(
    getActionType('updateSlugSuccess'),
    props<{ payload: { id: number; offer: OfferFull } }>(),
  ),
  updateSlugFailed: createAction(
    getActionType('updateSlugFailed'),
    props<{ payload: { id: number; error: HttpErrorResponse } }>(),
  ),

  insertDataFromTemplate: createAction(
    getActionType('insertDataFromTemplate'),
    props<{
      payload: {
        offerId: number;
        jobTemplateId: number;
        insertMethod: InsertFromTemplateMethod;
        placementsMovements?: PlacementMovement[];
      };
    }>(),
  ),
  insertDataFromTemplateSuccess: createAction(
    getActionType('insertDataFromTemplateSuccess'),
    props<{ payload: { data: { offer: OfferFull } } }>(),
  ),
  insertDataFromTemplateFailed: createAction(
    getActionType('insertDataFromTemplateFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),

  switchJobsToProsemirror: createAction(getActionType('switchJobsToProsemirror')),
  switchJobsToProsemirrorSuccess: createAction(getActionType('switchJobsToProsemirrorSuccess')),
  switchJobsToProsemirrorFailed: createAction(
    getActionType('switchJobsToProsemirrorFailed'),
    props<{ payload: { error: HttpErrorResponse } }>(),
  ),
};

export { actions as JobsWrapperActions };
